import { faShoppingCart, faTrash } from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import {
  ConfirmDialog,
  useConfirmDialogComponent,
} from 'src/components/ConfirmDialog';
import { Dialog } from 'src/components/Dialog';
import { Divider } from 'src/components/Divider';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Typography } from 'src/components/Typography';
import { useCart } from 'src/contexts/CartContext';
import { externalLinks } from 'src/externalLinks';
import { CartItemRow } from 'src/shared/CartItemRow';

interface CartDialogProps {
  open: boolean;
  onClose: () => void;
}

export const CartDialog: React.FC<CartDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'cart.dialog' });
  const { cart, totalItems, totalPrice, clearCart } = useCart();
  const [confirmDialogProps, confirm] = useConfirmDialogComponent();
  const [shouldNavigateToCheckout, setShouldNavigateToCheckout] =
    useState(false);

  const handleCheckout = useCallback(() => {
    onClose();
    setShouldNavigateToCheckout(true);
  }, [onClose]);

  useEffect(() => {
    if (shouldNavigateToCheckout) {
      window.location.href = externalLinks.cart;
      setShouldNavigateToCheckout(false);
    }
  }, [shouldNavigateToCheckout]);

  const handleClearCart = async () => {
    if (
      await confirm({
        title: t('clearCartConfirm'),
        message: t('clearCartMessage'),
        confirmText: t('clearCart'),
        cancelText: t('cancel'),
        confirmColor: 'error',
      })
    ) {
      clearCart();
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price / 100); // Assuming price is in cents
  };

  const cartItems = cart?.cartItems || [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('title')}
      maxWidth="sm"
      fullWidth
      actions={[
        {
          label: t('continueShopping'),
          variant: 'bordered',
          onClick: onClose,
        },
        {
          label: t('checkout'),
          variant: 'prominent',
          color: 'primary',
          disabled: totalItems === 0,
          onClick: handleCheckout,
        },
      ]}
      rightAdornment={
        totalItems > 0 && (
          <IconButton onClick={handleClearCart} size="small">
            <Icon icon={faTrash} size="small" />
          </IconButton>
        )
      }
    >
      <Box>
        {cartItems.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            py={4}
          >
            <Icon
              icon={faShoppingCart}
              size="large"
              color="disabled"
              sx={{ mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('emptyTitle')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              {t('emptyMessage')}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Cart Items */}
            <Box>
              {cartItems.map((cartItem, index) => (
                <CartItemRow
                  key={`${cartItem.itemId}-${cartItem.itemType}-${index}`}
                  cartItem={cartItem}
                  cartItemIndex={index}
                />
              ))}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Cart Summary */}
            <Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('totalItems')}
                </Typography>
                <Typography variant="body2">{totalItems}</Typography>
              </Box>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6">{t('total')}</Typography>
                <Typography variant="h6" color="primary">
                  {formatPrice(totalPrice)}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
      <ConfirmDialog {...confirmDialogProps} />
    </Dialog>
  );
};
